import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  AfterViewInit,
  HostListener,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { Location } from '@angular/common';
import { HeaderComponent } from './header/header.component';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { BreadcrumbComponent } from './breadcrumb/breadcrumb.component';
import { ToastNotificationComponent } from './toast-notification/toast-notification.component';
import { Subject, takeUntil } from 'rxjs';
import { MaintenanceService } from '@frontend/feature-maintenance';
import { MaintenancePageComponent } from '@frontend/feature-maintenance';
import { LocalizeRouterPipe } from '@frontend/util-router-history';
import { TranslateModule } from '@ngx-translate/core';
import { OffCanvasStateService } from '@frontend/util-off-canvas';
import { TooltipModule } from '@schueco/schueco-ui-components';
import { RouterHistoryService, isDocumentDetailsRoute } from '@frontend/util-router-history';

@Component({
  selector: 'dc-feature-shell',
  standalone: true,
  imports: [
    HeaderComponent,
    RouterModule,
    BreadcrumbComponent,
    MaintenancePageComponent,
    ToastNotificationComponent,
    LocalizeRouterPipe,
    TooltipModule,
    TranslateModule,
  ],
  templateUrl: './feature-shell.component.html',
  styleUrl: './feature-shell.component.scss',
})
export class FeatureShellComponent implements OnInit, AfterViewInit, OnDestroy {
  private maintenanceService = inject(MaintenanceService);
  private router = inject(Router);
  private location = inject(Location);
  private offCanvasStateService = inject(OffCanvasStateService);
  private elementRef = inject(ElementRef);
  private routerHistoryService = inject(RouterHistoryService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  isServiceUnavailable = false;
  isDocumentDetailsRoute = false;
  isHeaderPanelVisible = false;
  isLeftOffCanvasOpen = true;
  isMobile = false;
  canGoBackValue = false;
  isInitialLoad = true;

  ngOnInit(): void {
    // Check initial screen size
    this.checkScreen();
    window.addEventListener('resize', this.checkScreen.bind(this));

    // Initialize button bar height
    this.updateButtonBarHeight();

    this.routerHistoryService.canGoBack$.pipe(takeUntil(this.destroy$)).subscribe((canGoBack) => {
      this.canGoBackValue = canGoBack;
      this.cdr.markForCheck();
    });

    // Check initial route on component load
    this.checkIfDocumentDetailsRoute(this.router.url);

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.checkIfDocumentDetailsRoute(event.url);
      }
    });

    this.maintenanceService.serviceUnavailable$.pipe(takeUntil(this.destroy$)).subscribe((isServiceUnavailable) => {
      this.isServiceUnavailable = isServiceUnavailable;
    });

    // Subscribe to off-canvas state changes
    this.offCanvasStateService.leftOffCanvasOpen$.pipe(takeUntil(this.destroy$)).subscribe((isOpen) => {
      this.isLeftOffCanvasOpen = isOpen;
      this.updateButtonBarHeight();
      // Update header height when off-canvas changes (might affect header size)
      setTimeout(() => this.updateHeaderHeight(), 100);
    });
  }

  ngAfterViewInit(): void {
    this.updateHeaderHeight();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('resize', this.checkScreen.bind(this));
  }

  private updateButtonBarHeight(): void {
    // Setze CSS-Variable für Button-Bar Höhe
    // Mobile: immer 2.5rem (keine Anpassung nötig)
    // Desktop Expanded: 1 Button = 2.5rem
    // Desktop Collapsed: toggle-group (3rem) + 2 Buttons (2x 2.5rem) = 8rem
    const height = this.isMobile ? '2.5rem' : this.isLeftOffCanvasOpen ? '2.5rem' : '8rem';
    document.documentElement.style.setProperty('--button-bar-height', height);
  }

  private updateHeaderHeight(): void {
    const headerElement = this.elementRef.nativeElement.querySelector('dc-header');
    if (headerElement) {
      const height = headerElement.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }

    // Breadcrumb-Höhe ebenfalls setzen
    const breadcrumbElement = this.elementRef.nativeElement.querySelector('dc-breadcrumb');
    if (breadcrumbElement) {
      const height = breadcrumbElement.offsetHeight;
      document.documentElement.style.setProperty('--breadcrumb-height', `${height}px`);
    }

    // Gesamthöhe des Overlay-Containers berechnen
    const overlayElement = this.elementRef.nativeElement.querySelector('.header-panel-overlay');
    if (overlayElement) {
      const height = overlayElement.offsetHeight;
      document.documentElement.style.setProperty('--overlay-height', `${height}px`);
    }
  }

  private checkScreen(): void {
    this.isMobile = window.innerWidth <= 768;
    this.updateButtonBarHeight();
    this.updateHeaderHeight();
  }

  toggleHeaderPanel() {
    this.isHeaderPanelVisible = !this.isHeaderPanelVisible;
    if (this.isInitialLoad) {
      this.isInitialLoad = false;
    }
    // Update heights when visibility changes
    setTimeout(() => this.updateHeaderHeight(), 100);
  }

  goBack() {
    // Check if catalog toolbar navigation is active. If so, use history.go to skip all iframe history entries
    if (this.routerHistoryService.isToolbarNavigationActive()) {
      const count = this.routerHistoryService.getToolbarNavigationCount();

      // Reset toolbar navigation flag before going back
      this.routerHistoryService.setToolbarNavigationActive(false);

      // Go back by count + 1 to skip all iframe entries and exit catalog +1 because we need to skip the current page too
      history.go(-(count + 1));
    } else {
      this.location.back();
    }
  }

  canGoBack(): boolean {
    return this.canGoBackValue;
  }

  goHome() {
    this.router.navigate(['/']);
  }

  private checkIfDocumentDetailsRoute(url: string): void {
    const wasDocumentDetailsRoute = this.isDocumentDetailsRoute;
    this.isDocumentDetailsRoute = isDocumentDetailsRoute(url);

    // Header ausblenden sobald eine Detailseite betreten wird
    if (this.isDocumentDetailsRoute) {
      this.isHeaderPanelVisible = false;
      if (!wasDocumentDetailsRoute) {
        this.isInitialLoad = true;
      }
    }
  }

  //Logik zum Schließen des Header-Panels bei Klick außerhalb oder Fensterfokusverlust
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Nur wenn Header sichtbar ist und auf document-details Route
    if (!this.isHeaderPanelVisible || !this.isDocumentDetailsRoute) {
      return;
    }

    // Type-Guard: event.target kann auch Text-Node oder Document sein
    if (!(event.target instanceof Element)) {
      return;
    }

    const clickedElement = event.target;
    const overlayContainer = this.elementRef.nativeElement.querySelector('.header-panel-overlay');
    const buttonBar = this.elementRef.nativeElement.querySelector('.header-panel-buttons');

    // Prüfe ob Klick in einem Dropdown-Menü war
    const isInDropdown =
      clickedElement.closest('.cdk-overlay-container') ||
      clickedElement.closest('.sco-dropdown-navigation__sub-links-wrapper') ||
      clickedElement.closest('.sco-dropdown-navigation__sub-links-container') ||
      clickedElement.closest('.sco-dropdown__menu') ||
      clickedElement.closest('sco-dropdown') ||
      clickedElement.closest('sco-dropdown-navigation');

    if (isInDropdown) {
      // Klick war in einem Dropdown-Menü, Header nicht schließen
      return;
    }

    // Prüfe ob Klick außerhalb des Overlay-Containers und Button-Bar war
    if (
      overlayContainer &&
      !overlayContainer.contains(clickedElement) &&
      buttonBar &&
      !buttonBar.contains(clickedElement)
    ) {
      this.isHeaderPanelVisible = false;
    }
  }

  @HostListener('window:blur')
  onWindowBlur(): void {
    // Wenn das Fenster den Fokus verliert (z.B. durch iframe-Klick), schließe Header
    if (this.isHeaderPanelVisible && this.isDocumentDetailsRoute) {
      this.isHeaderPanelVisible = false;
    }
  }
}
